import { useEffect, useRef, useState } from 'react'

import { NotificationCategory, NotificationType } from '../api/create-notification'

import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'

export interface Notification {
  id: string
  user_id: string | null
  type: NotificationType
  category: NotificationCategory
  title: string | null
  message: string
  metadata: Record<string, unknown>
  related_entity_id: string | null
  related_entity_type: string | null
  action_url: string | null
  expires_at: string | null
  is_read: boolean | null
  created_at: string | null
}

interface UseNotificationsOptions {
  uid: string
}

// Generate a unique ID for each hook instance
let hookInstanceCounter = 0

export const useNotifications = ({ uid }: UseNotificationsOptions) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isPending, setIsPending] = useState(false)
  const instanceId = useRef(++hookInstanceCounter).current

  useEffect(() => {
    if (!uid) {
      setNotifications([])
      setIsPending(false)
      return
    }

    // Fetch initial notifications
    const fetchNotifications = async () => {
      setIsPending(true)
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', uid)
        .range(0, 3)
        .order('is_read', { ascending: true })
        .order('created_at', { ascending: false })

      setIsPending(false)

      if (error) {
        logger.error('Error fetching notifications', {
          errorMessage: error.message,
        })
        return
      }

      setNotifications((data as unknown as Notification[]) || [])
    }

    fetchNotifications()

    // Listen for new notifications
    const channelName = `notifications:${uid}:${instanceId}`
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${uid}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev])
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${uid}`,
        },
        (payload) => {
          setNotifications((prev) => prev.map((n) => (n.id === (payload.new as Notification).id ? (payload.new as Notification) : n)))
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(subscription)
    }
  }, [uid, instanceId])

  return {
    notifications,
    isPending,
    unread: notifications.filter((n) => !n.is_read).length,
    hasUnread: notifications.some((n) => !n.is_read),
  }
}
